import LegalLayout, {
  LegalSection,
  InfoList,
  Columns,
  Subhead,
  Bullets,
  Note,
} from "@/components/ttt/LegalLayout"
import type { TttChrome } from "@/components/ttt/types"

interface Props {
  chrome: TttChrome
  lastUpdated: string
}

const mail = "turbotimetravel@gmail.com"

export default function MentionsLegales({ chrome, lastUpdated }: Props) {
  return (
    <LegalLayout
      chrome={chrome}
      title="Mentions légales"
      subtitle="Informations légales obligatoires concernant le site ttt-editions.fr et l'association TTT Corp."
      lastUpdated={lastUpdated}
      cta={{
        title: "Une question sur nos mentions légales ?",
        subtitle: "Notre équipe est disponible pour vous répondre.",
        secondaryHref: chrome.paths.confidentiality,
        secondaryLabel: "Politique de confidentialité",
      }}
    >
      <LegalSection title="1. Éditeur du site">
        <InfoList
          items={[
            { label: "Dénomination", value: "TTT Corp" },
            { label: "Forme juridique", value: "Association loi 1901" },
            { label: "Objet social", value: "Maison d'édition de bandes dessinées" },
            { label: "Président", value: "Yann Cavalier" },
            { label: "RNA", value: "W442019112" },
            {
              label: "Siège social",
              value: (
                <>
                  14 Rue Sanlecque
                  <br />
                  44000 Nantes, France
                </>
              ),
            },
          ]}
        />
      </LegalSection>

      <LegalSection title="2. Coordonnées">
        <InfoList
          items={[
            {
              label: "E-mail",
              value: (
                <a href={`mailto:${mail}`} className="text-primary hover:underline">
                  {mail}
                </a>
              ),
            },
            {
              label: "Adresse",
              value: (
                <>
                  14 Rue Sanlecque
                  <br />
                  44000 Nantes
                </>
              ),
            },
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Hébergement">
        <InfoList
          items={[
            { label: "Hébergeur", value: "Clever Cloud SAS" },
            {
              label: "Adresse",
              value: (
                <>
                  4 rue Voltaire
                  <br />
                  44000 Nantes, France
                </>
              ),
            },
            {
              label: "Site web",
              value: (
                <a
                  href="https://www.clever-cloud.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  www.clever-cloud.com
                </a>
              ),
            },
            { label: "SIRET", value: "524 172 699 00034" },
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Directeur de publication">
        <p>Le directeur de la publication du site ttt-editions.fr est :</p>
        <InfoList
          items={[
            { label: "Responsable", value: "Jason Sorin / Yann Cavalier" },
            { label: "Fonction", value: "Président de TTT Corp" },
            { label: "Périmètre", value: "Responsable des contenus publiés" },
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Propriété intellectuelle">
        <p>
          L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, plans,
          dessins, etc.) est protégé par les droits de propriété intellectuelle.
        </p>
        <Columns>
          <div>
            <Subhead>Reproduction interdite</Subhead>
            <p className="text-content/80 text-sm">
              Toute reproduction totale ou partielle sans autorisation est strictement interdite.
            </p>
          </div>
          <div>
            <Subhead>Usage personnel autorisé</Subhead>
            <p className="text-content/80 text-sm">
              Consultation et copie privée pour usage personnel uniquement.
            </p>
          </div>
        </Columns>
        <Note title="Droits d'auteur">
          Tous les éléments de ce site sont la propriété exclusive de TTT Corp ou font l'objet d'une
          autorisation d'utilisation.
        </Note>
      </LegalSection>

      <LegalSection title="6. Responsabilité et disclaimer">
        <p>
          TTT Corp s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur
          ce site. Cependant :
        </p>
        <Bullets
          items={[
            "Les informations sont données à titre indicatif et peuvent évoluer",
            "TTT Corp ne peut garantir l'absence d'erreurs ou d'omissions",
            "L'utilisation des informations se fait sous votre responsabilité",
            "TTT Corp ne peut être tenu responsable des dommages indirects",
          ]}
        />
        <Note title="Clause de non-responsabilité">
          L'utilisateur reconnaît utiliser ces informations sous sa propre responsabilité et dégage
          TTT Corp de toute responsabilité à ce titre.
        </Note>
      </LegalSection>

      <LegalSection title="7. Liens hypertextes">
        <p>
          Ce site peut contenir des liens vers d'autres sites web. TTT Corp n'exerce aucun contrôle
          sur ces sites et décline toute responsabilité quant à leur contenu.
        </p>
        <Columns>
          <div>
            <Subhead>Liens sortants</Subhead>
            <p className="text-content/80 text-sm">
              Les liens vers des sites externes sont fournis à titre informatif uniquement.
            </p>
          </div>
          <div>
            <Subhead>Demande de lien</Subhead>
            <p className="text-content/80 text-sm">
              Tout lien vers notre site doit faire l'objet d'une autorisation préalable.
            </p>
          </div>
        </Columns>
      </LegalSection>

      <LegalSection title="8. Cookies et collecte de données">
        <p>
          Ce site peut utiliser des cookies pour améliorer l'expérience utilisateur et réaliser des
          statistiques de visite.
        </p>
        <Bullets
          items={[
            <>
              <span className="text-contrast">Cookies techniques</span> — nécessaires au
              fonctionnement du site
            </>,
            <>
              <span className="text-contrast">Analytics</span> — statistiques anonymisées de
              fréquentation
            </>,
            <>
              <span className="text-contrast">Contrôle utilisateur</span> — possibilité de
              désactiver les cookies
            </>,
          ]}
        />
        <Note>
          Pour plus d'informations, consultez notre{" "}
          <a href={chrome.paths.confidentiality} className="text-primary hover:underline">
            politique de confidentialité
          </a>
          .
        </Note>
      </LegalSection>

      <LegalSection title="9. Droit applicable et juridiction">
        <Columns>
          <div>
            <Subhead>Droit applicable</Subhead>
            <p className="text-content/80 text-sm">
              Les présentes mentions légales sont soumises au droit français.
            </p>
          </div>
          <div>
            <Subhead>Juridiction compétente</Subhead>
            <p className="text-content/80 text-sm">
              En cas de litige, les tribunaux de Nantes seront seuls compétents.
            </p>
          </div>
        </Columns>
        <Note title="Règlement des différends">
          En cas de différend, nous privilégions la résolution amiable. À défaut d'accord, le litige
          sera porté devant les tribunaux compétents de Nantes.
        </Note>
      </LegalSection>
    </LegalLayout>
  )
}
